/* global globalThis */
import axios from "axios";
import { clientDeviceId, clientDeviceType } from "./clientIdentity";
import { notifySystemNoticesChanged } from '../services/systemNoticeEvents';

export const TOKEN_STORAGE_KEY = "rv_token";
export const USER_STORAGE_KEY = "rv_user";

const resolveBaseUrl = () => {
  const explicitBase = process.env.REACT_APP_API_BASE_URL;
  if (explicitBase) {
    return explicitBase.replace(/\/+$/, "");
  }

  const defaultPort = process.env.REACT_APP_API_PORT ?? "7290";

  if (typeof window !== "undefined") {
    const { protocol, hostname, port: browserPort } = window.location;

    if (process.env.NODE_ENV !== "production" && browserPort === "3000") {
      return "/api";
    }

    const portSegment = defaultPort ? `:${defaultPort}` : "";
    return `${protocol}//${hostname}${portSegment}/api`;
  }

  return defaultPort
    ? `http://localhost:${defaultPort}/api`
    : "http://localhost/api";
};

const api = axios.create({
  baseURL: resolveBaseUrl(),
});

let partitionToken = null, readPartition = null;

const belongsToCurrentSession = (config) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const headers = config?.headers;
  const authorization = headers?.get?.('Authorization') ?? headers?.Authorization ?? headers?.authorization;
  return Boolean(token) && authorization === `Bearer ${token}`;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (config.expectedSessionToken !== undefined && config.expectedSessionToken !== token) {
    const error = new Error("Session changed before request transmission.");
    error.code = "ERR_CANCELED";
    throw error;
  }
  if (token) {
    // Ensure headers object exists
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
    config.headers["X-CAD-Client-Id"] = clientDeviceId();
    config.headers["X-CAD-Device-Type"] = clientDeviceType();
    // Older installed workers cache GETs by URL. Partition reads during upgrade
    // without putting any credential in the URL.
    if ((config.method || 'get').toLowerCase() === 'get') {
      if (partitionToken !== token) {
        partitionToken = token;
        const bytes = new Uint8Array(16);
        globalThis.crypto.getRandomValues(bytes);
        readPartition = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
      }
      if (config.params instanceof URLSearchParams) {
        config.params = new URLSearchParams(config.params);
        config.params.set('__cad_read', readPartition);
      } else config.params = { ...config.params, __cad_read: readPartition };
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (!['get', 'head', 'options'].includes((response.config?.method || 'get').toLowerCase()) &&
        /^\/?(?:announcements(?:\/|$)|status\/maintenance(?:\?|$)|system-settings\/announcement(?:\?|$))/.test(response.config?.url || '')) {
      notifySystemNoticesChanged();
    }
    const replacement = response.headers?.['x-ranger-access-token'];
    if (replacement && belongsToCurrentSession(response.config)) {
      localStorage.setItem(TOKEN_STORAGE_KEY, replacement);
    }
    return response;
  },
  (error) => {
    // Skip automatic logout if the request was marked to suppress it
    const skipAutoLogout = error.config?.skipAutoLogout;
    const currentPath = window.location.pathname || "";
    const isPublicApplyPath = currentPath === "/apply" || currentPath.startsWith("/apply/");
    
    if (error.response?.status === 401 && !skipAutoLogout && !isPublicApplyPath && belongsToCurrentSession(error.config)) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);

      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

export default api;
